import type { FC } from "react";
import { translate } from "app/filters/i18nFilter";
import { useWebsiteKeywordsPageTableTopContext } from "../../WebsiteKeywordsPageContext";
import { MultiIncludeExcludeWebsites } from "./MultiIncludeExcludeWebsites";
import { MultiIncludeExcludeKeywords } from "./MultiIncludeExcludeKeywords";
import { MultiIncludeExcludeKeywordGroupsTextField, MultiIncludeExcludeKeywordsTextField } from "./utils";
import type { MultiIncludeExcludePopupContentProps } from "./types";

interface MultiIncludeExcludeFilterProps {
    withMatchType?: boolean;
    maxRules?: number;
    getRuleSeparatorText?: MultiIncludeExcludePopupContentProps["getRuleSeparatorText"];
}

interface MultiIncludeExcludeKeywordFilterProps extends MultiIncludeExcludeFilterProps {
    withKeywordGroups?: boolean;
}

export const MultiIncludeExcludeWebsitesFilter: FC<MultiIncludeExcludeFilterProps> = ({
    withMatchType = true,
    maxRules,
    getRuleSeparatorText,
}) => {
    const { tableFilters, onMultiIncludeExcludeWebsiteChange } = useWebsiteKeywordsPageTableTopContext();

    const rules = tableFilters.multiIncludeExcludeWebsites ? JSON.parse(tableFilters.multiIncludeExcludeWebsites) : [];

    const getButtonKey = (rules: any[]) =>
        rules.length === 1
            ? translate("multi.include.exclude.websites.button.single.rule")
            : translate("multi.include.exclude.websites.button.multiple.rules", {
                  count: rules.length,
              });

    return (
        <MultiIncludeExcludeWebsites
            rules={rules}
            maxRules={maxRules}
            getButtonText={(rules) =>
                rules?.length ? getButtonKey(rules) : translate("multi.include.exclude.websites.button.empty.state")
            }
            onMultiIncludeExcludeChange={onMultiIncludeExcludeWebsiteChange}
            withMatchType={withMatchType}
            getRuleSeparatorText={getRuleSeparatorText}
        />
    );
};

export const MultiIncludeExcludeKeywordsFilter: FC<MultiIncludeExcludeKeywordFilterProps> = ({
    withMatchType = true,
    withKeywordGroups = true,
    maxRules,
    getRuleSeparatorText,
}) => {
    const { tableFilters, onMultiIncludeExcludeKeywordChange } = useWebsiteKeywordsPageTableTopContext();

    const rules = tableFilters.multiIncludeExcludeKeywords ? JSON.parse(tableFilters.multiIncludeExcludeKeywords) : [];

    const getButtonKey = (rules: any[]) =>
        rules.length === 1
            ? translate("multi.include.exclude.keywords.button.single.rule")
            : translate("multi.include.exclude.keywords.button.multiple.rules", {
                  count: rules.length,
              });

    return (
        <MultiIncludeExcludeKeywords
            rules={rules}
            maxRules={maxRules}
            getButtonText={(rules) =>
                rules?.length
                    ? getButtonKey(rules)
                    : translate(
                          withKeywordGroups
                              ? "multi.include.exclude.keywords.button.empty.state"
                              : "multi.include.exclude.keyword.button.empty.state",
                      )
            }
            onMultiIncludeExcludeChange={onMultiIncludeExcludeKeywordChange}
            getTextField={
                withKeywordGroups ? MultiIncludeExcludeKeywordGroupsTextField : MultiIncludeExcludeKeywordsTextField
            }
            withMatchType={withMatchType}
            getRuleSeparatorText={getRuleSeparatorText}
        />
    );
};
